'use strict';

import { getCookie } from '@susu/custom-made/utils/utils';

// Your segment website source write key
const WEBSITE_WRITE_KEY = process.env.NEXT_PUBLIC_CM_segmentKey;
// Update to your CNAME if using Indomain Instrumentation
const INDOMAIN_INSTRUMENTATION_URL = `https://cdn.segment.com/analytics.js/v1/${WEBSITE_WRITE_KEY}/analytics.min.js`;
// Segment Analytics.js SDK without default .load calls
export function loadAnalytics() {
  var analytics = (window.analytics = window.analytics || []);
  if (!analytics.initialize) {
    if (analytics.invoked) {
      window.console && console.error && console.error('Segment snippet included twice.');
    } else {
      analytics.invoked = !0;
      analytics.methods = [
        'trackSubmit',
        'trackClick',
        'trackLink',
        'trackForm',
        'pageview',
        'identify',
        'reset',
        'group',
        'track',
        'register',
        'ready',
        'alias',
        'debug',
        'page',
        'once',
        'off',
        'on',
        'addSourceMiddleware',
        'addIntegrationMiddleware',
        'setAnonymousId',
        'addDestinationMiddleware',
      ];
      analytics.factory = function (e) {
        return function () {
          var t = Array.prototype.slice.call(arguments);
          t.unshift(e);
          analytics.push(t);
          return analytics;
        };
      };
      for (var e = 0; e < analytics.methods.length; e++) {
        var key = analytics.methods[e];
        analytics[key] = analytics.factory(key);
      }
      analytics.load = function (_key, e) {
        var t = document.createElement('script');
        t.type = 'text/javascript';
        t.async = !0;
        t.src = INDOMAIN_INSTRUMENTATION_URL;
        t.setAttributeNode(document.createAttribute('data-ot-ignore'));
        t.setAttribute('class', 'optanon-category-C0001');
        var n = document.getElementsByTagName('script')[0];
        n.parentNode.insertBefore(t, n);
        analytics._loadOptions = e;
      };
      analytics._writeKey = WEBSITE_WRITE_KEY;
      analytics.SNIPPET_VERSION = '4.15.3';
      analytics.load(WEBSITE_WRITE_KEY);
    }
  }
  // Call events when analytics.js is loaded
  window.analytics.ready(async () => {
    await registerAndCall();
  });
}

/**
 * Transforms consent object into a different structure.
 * @param {string} consent - The consent object.
 * @returns {Object} The transformed consent object with category preferences.
 */
const transformConsent = consent => {
  // Check if gtag is defined and has a valid definition
  const categories = consent.split(',').reduce((acc, category) => {
    const [key, value] = category.split(':');
    acc[key] = value === '1';
    return acc;
  }, {});

  return {
    categoryPreferences: categories,
  };
};

/**
 * Parses consent information from the OptanonConsent cookie.
 * @returns {string} The consent information in the format 'CXXXX:Y,CXXXX:Y,...'.
 */
const parseOptanonConsent = () => {
  let consentOnetrust = 'C0001:0,C0003:0,C0004:0,C0002:0';
  const optanonConsentCookie = getCookie('OptanonConsent');

  if (optanonConsentCookie !== '') {
    const decodedConsentCookie = decodeURIComponent(optanonConsentCookie);
    const groupsIndex = decodedConsentCookie.indexOf('&groups=');
    if (groupsIndex !== -1) {
      const endIndex = decodedConsentCookie.indexOf('&', groupsIndex + 1);
      consentOnetrust = decodedConsentCookie.substring(
        groupsIndex + '&groups='.length,
        endIndex !== -1 ? endIndex : undefined,
      );
    }
  }
  return consentOnetrust;
};

/**
 * Identifies the user and sends relevant traits.
 * @param {Object} consentOnetrust - The consentOnetrust data.
 */
function identifyUser(consentOnetrust) {
  const traits = {
    consent_onetrust_web: consentOnetrust,
    ga_session_id: localStorage.getItem('session_id') || null,
    ga_client_id: localStorage.getItem('client_id') || null,
    ga_session_number: localStorage.getItem('session_number') || null,
  };

  const userID = getCookie('uid');
  if (userID && !window.sessionStorage.getItem('known_identity')) {
    window.sessionStorage.setItem('known_identity', 'set');
    window.analytics?.identify(userID, traits);
  } else if (!window.sessionStorage.getItem('anonymous_identity')) {
    window.sessionStorage.setItem('anonymous_identity', 'set');
    window.analytics?.identify(traits);
  }
}

/**
 * Registers and calls the Analytics Integration plugin.
 */
async function registerAndCall() {
  const consentOnetrust = parseOptanonConsent();
  await registerAnalytics();
  identifyUser(consentOnetrust);
  window.dataLayer?.push({ event: 'Segment Loaded' });
}

/**
 * Registers the Segment Integration plugin.
 * @param {Object} enrichment - The data for enrichment.
 */

async function registerAnalytics() {
  const gaCookieId = process.env.NEXT_PUBLIC_CM_env === 'prd' ? 'G-2JHV4W0XRX' : 'G-0XNN95Z7VJ';

  // registering plugin to analytics
  await window.analytics.register({
    name: 'Suitsupply Integration Plugin',
    version: '0.1.0',
    type: 'enrichment',
    isLoaded: () => true,
    load: loadData,
    page: enrichPage,
    track: enrichTrack,
    identify: enrichIdentify,
  });

  /**
   * Loads data.
   * @returns {void} A promise indicating loading completion.
   */
  async function loadData() {
    // get ga_client_id
    const clientIdPromise = getSessionData('client_id');
    // get session_id
    const sessionIdPromise = getSessionData('session_id');
    // get session_number
    const sessionNumPromise = getSessionData('session_number');

    clientIdPromise.then(clientId => {
      localStorage.setItem('client_id', clientId);
    });

    sessionIdPromise.then(sessionId => {
      localStorage.setItem('session_id', sessionId);
    });

    sessionNumPromise.then(sessionNumber => {
      localStorage.setItem('session_number', sessionNumber);
    });

    Promise.all([sessionIdPromise, sessionNumPromise, clientIdPromise]).then(function (sessionData) {
      console.log('session ID: ' + sessionData[0]);
      console.log('session Number: ' + sessionData[1]);
      console.log('client id: ' + sessionData[2]);
    });
  }

  /**
   * Retrieves session data from GTAG.
   * @param {string} key - The key to retrieve.
   * @returns {Promise<string>} The retrieved session data.
   */
  function getSessionData(key) {
    return new Promise(resolve => {
      if (typeof gtag === 'function') {
        gtag('get', gaCookieId, key, resolve);
      }
    });
  }

  /**
   * Updates the event properties with user account information.
   * This function sets the user ID and login state in the event properties,
   * and ensures that each product in the event properties contains the same information.
   * @param {Object} ctx - The context object containing event properties.
   */
  function updateUserProperties(ctx) {
    const ACCOUNT_NUMBER = window.sessionLayer?.uid ? window.sessionLayer.uid : '';
    const isLoggedIn = window.sessionLayer?.login_state;

    ctx.event.properties.user_id = ACCOUNT_NUMBER;
    ctx.event.properties.logged_in = isLoggedIn ? 'true' : 'false';

    ctx.event.properties?.products?.forEach(product => {
      if (product.user_id) {
        product.user_id = ACCOUNT_NUMBER;
      }
      product.logged_in = isLoggedIn ? 'true' : 'false';
    });
  }
  /**
   * Updates the context object with enrichment data.
   * @param {Object} ctx - The context object.
   */
  function updateContext(ctx) {
    updateUserProperties(ctx);
    ctx.event.properties.ga_client_id = localStorage.getItem('client_id') || null;
    ctx.event.properties.ga_session_id = localStorage.getItem('session_id') || null;
    ctx.event.properties.ga_session_number = localStorage.getItem('session_number') || null;
    const consentOnetrust = parseOptanonConsent();
    ctx.event.context.consent = transformConsent(consentOnetrust);
    ctx.event.context.ip = '0.0.0.0';
    ctx.updateEvent(ctx);
  }

  /**
   * Enriches the page context.
   * @param {Object} ctx - The context object.
   * @returns {Object} The updated context object.
   */
  function enrichPage(ctx) {
    updateContext(ctx);
    return ctx;
  }

  /**
   * Enriches the track context.
   * @param {Object} ctx - The context object.
   * @returns {Object} The updated context object.
   */
  function enrichTrack(ctx) {
    updateContext(ctx);
    return ctx;
  }

  /**
   * Enriches the identify context.
   * @param {Object} ctx - The context object.
   * @returns {Object} The updated context object.
   */
  function enrichIdentify(ctx) {
    updateContext(ctx);
    return ctx;
  }
}
